import React, { useEffect, useMemo, useState } from 'react';
import {
  Add as PlusIcon,
  Edit as PencilIcon,
  Delete as TrashIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as XCircleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import {
  type CompetitorItem,
  addCompetitorAdvantage,
  removeCompetitorAdvantage,
  updateCompetitorAdvantage,
  addCompetitorWeakness,
  removeCompetitorWeakness,
  updateCompetitorWeakness,
  toggleCompetitorStatus
} from '../services/apiService';
import { CompetitorAdvantage, CompetitorWeakness } from '../types/competitor';
import AdvantageModal from './AdvantageModal';
import WeaknessModal from './WeaknessModal';
import { Button } from './ui/Button';

interface CompetitorManagementProps {
  competitors: CompetitorItem[];
  searchTerm: string;
  onRefresh: () => void;
  onEdit: (competitor: CompetitorItem) => void;
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
  selectedCompetitorIds: Set<string>;
  onToggleCompetitor: (competitorId: string) => void;
  focusIncomplete?: boolean;
}

const needsDetails = (competitor: CompetitorItem) =>
  competitor.isActive && (competitor.advantages.length === 0 || competitor.weaknesses.length === 0);

const CompetitorManagement: React.FC<CompetitorManagementProps> = ({
  competitors,
  searchTerm,
  onRefresh,
  onEdit,
  setError,
  setSuccess,
  selectedCompetitorIds,
  onToggleCompetitor,
  focusIncomplete = false
}) => {
  const [expandedCompetitors, setExpandedCompetitors] = useState<Set<string>>(new Set());
  const [advantageModal, setAdvantageModal] = useState<{
    isOpen: boolean;
    competitorId: string | null;
    advantage: CompetitorAdvantage | null;
  }>({ isOpen: false, competitorId: null, advantage: null });
  const [weaknessModal, setWeaknessModal] = useState<{
    isOpen: boolean;
    competitorId: string | null;
    weakness: CompetitorWeakness | null;
  }>({ isOpen: false, competitorId: null, weakness: null });

  const firstIncompleteId = useMemo(
    () => competitors.find(needsDetails)?._id || null,
    [competitors]
  );

  useEffect(() => {
    if (!focusIncomplete || !firstIncompleteId) return;
    setExpandedCompetitors((current) => new Set(current).add(firstIncompleteId));
    window.requestAnimationFrame(() => {
      document.getElementById(`competitor-${firstIncompleteId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [firstIncompleteId, focusIncomplete]);

  const toggleExpanded = (competitorId: string) => {
    const newExpanded = new Set(expandedCompetitors);
    if (newExpanded.has(competitorId)) {
      newExpanded.delete(competitorId);
    } else {
      newExpanded.add(competitorId);
    }
    setExpandedCompetitors(newExpanded);
  };

  const handleToggleStatus = async (competitor: CompetitorItem) => {
    try {
      await toggleCompetitorStatus(competitor._id);
      setSuccess(`${competitor.name} status updated`);
      onRefresh();
    } catch (error) {
      setError('Failed to toggle competitor status');
    }
  };

  const handleSaveAdvantage = async (advantage: Partial<CompetitorAdvantage>) => {
    if (!advantageModal.competitorId) return;

    try {
      if (advantageModal.advantage?._id) {
        // Update existing advantage
        await updateCompetitorAdvantage(
          advantageModal.competitorId,
          advantageModal.advantage._id,
          advantage
        );
        setSuccess('Advantage updated successfully');
      } else {
        // Add new advantage
        await addCompetitorAdvantage(advantageModal.competitorId, advantage);
        setSuccess('Advantage added successfully');
      }
      onRefresh();
    } catch (error) {
      setError('Failed to save advantage');
    }
  };

  const handleRemoveAdvantage = async (competitorId: string, advantageId: string) => {
    try {
      await removeCompetitorAdvantage(competitorId, advantageId);
      setSuccess('Advantage removed successfully');
      onRefresh();
    } catch (error) {
      setError('Failed to remove advantage');
    }
  };

  const handleSaveWeakness = async (weakness: Partial<CompetitorWeakness>) => {
    if (!weaknessModal.competitorId) return;

    try {
      if (weaknessModal.weakness?._id) {
        // Update existing weakness
        await updateCompetitorWeakness(
          weaknessModal.competitorId,
          weaknessModal.weakness._id,
          weakness
        );
        setSuccess('Weakness updated successfully');
      } else {
        // Add new weakness
        await addCompetitorWeakness(weaknessModal.competitorId, weakness);
        setSuccess('Weakness added successfully');
      }
      onRefresh();
    } catch (error) {
      setError('Failed to save weakness');
    }
  };

  const handleRemoveWeakness = async (competitorId: string, weaknessId: string) => {
    try {
      await removeCompetitorWeakness(competitorId, weaknessId);
      setSuccess('Weakness removed successfully');
      onRefresh();
    } catch (error) {
      setError('Failed to remove weakness');
    }
  };

  const filteredCompetitors = competitors.filter(competitor => {
    const searchLower = searchTerm.toLowerCase();
    return (
      competitor.name.toLowerCase().includes(searchLower) ||
      competitor.talking_points.toLowerCase().includes(searchLower) ||
      competitor.advantages.some(a => a.point.toLowerCase().includes(searchLower))
    );
  }).sort((left, right) => Number(needsDetails(right)) - Number(needsDetails(left)));

  return (
    <>
      <div className="space-y-4">
        {focusIncomplete && firstIncompleteId && (
          <div className="rounded-lg border border-warning bg-warning-tint p-3 text-sm text-ink" role="status">
            Profiles needing details are shown first. Add at least one advantage and one weakness when you have that context; the profile remains publishable meanwhile.
          </div>
        )}
        {filteredCompetitors.length === 0 ? (
          <div className="text-center py-8 bg-surface rounded-lg">
            <p className="text-ink-muted">
              {searchTerm
                ? `No competitors found matching "${searchTerm}"`
                : 'Add competitors your prospects mention. Apexia Labs will help reps respond when those names come up during calls.'}
            </p>
          </div>
        ) : (
          filteredCompetitors.map((competitor) => (
            <div
              key={competitor._id}
              id={`competitor-${competitor._id}`}
              className={`bg-surface rounded-lg shadow-sm border overflow-hidden ${focusIncomplete && needsDetails(competitor) ? 'border-warning' : 'border-hairline'}`}
            >
              {/* Competitor Header */}
              <div className="p-4 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      aria-label={`Select competitor ${competitor.name}`}
                      checked={selectedCompetitorIds.has(competitor._id)}
                      onChange={() => onToggleCompetitor(competitor._id)}
                      className="h-4 w-4 text-signature rounded border-ink-border focus:ring-signature"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      type="button"
                      aria-label={`${expandedCompetitors.has(competitor._id) ? 'Collapse' : 'Expand'} competitor ${competitor.name}`}
                      aria-expanded={expandedCompetitors.has(competitor._id)}
                      onClick={() => toggleExpanded(competitor._id)}
                      className="text-ink-muted hover:text-ink-secondary"
                    >
                      {expandedCompetitors.has(competitor._id) ? (
                        <ExpandLessIcon />
                      ) : (
                        <ExpandMoreIcon />
                      )}
                    </button>
                    <h3 className="text-sm font-medium text-ink">{competitor.name}</h3>
                    {needsDetails(competitor) && (
                      <span className="px-2 py-1 text-xs rounded-full bg-warning-tint text-warning">
                        Add comparison details
                      </span>
                    )}
                    <span className="px-2 py-1 text-xs rounded-full bg-surface-sunken text-ink-secondary">
                      {competitor.category.replace('_', ' ')}
                    </span>
                    {competitor.isActive ? (
                      <CheckCircleIcon className="text-success" fontSize="small" titleAccess="Active — available on live calls" />
                    ) : (
                      <XCircleIcon className="text-ink-muted" fontSize="small" titleAccess="Inactive — hidden from live calls" />
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      type="button"
                      aria-label={`${competitor.isActive ? 'Deactivate' : 'Activate'} competitor ${competitor.name}`}
                      onClick={() => handleToggleStatus(competitor)}
                      className={`px-3 py-1 rounded text-sm ${
                        competitor.isActive
                          ? 'bg-surface-sunken text-ink-secondary hover:bg-surface-subtle hover:text-ink'
                          : 'bg-signature text-on-signature hover:bg-signature-strong'
                      }`}
                    >
                      {competitor.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                    <Button
                      type="button"
                      variant="tertiary"
                      size="sm"
                      aria-label={`Edit competitor ${competitor.name}`}
                      onClick={() => onEdit(competitor)}
                      className="p-2 text-signature hover:text-signature-strong"
                    >
                      <PencilIcon fontSize="small" />
                    </Button>
                  </div>
                </div>
                <p className="mt-2 text-sm text-ink-secondary">{competitor.talking_points}</p>
              </div>

              {/* Expanded Content */}
              {expandedCompetitors.has(competitor._id) && (
                <div className="p-4 space-y-4 bg-surface-subtle">
                  {/* Advantages */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="flex items-center text-base font-medium text-ink">
                        <TrendingUpIcon className="mr-2 text-ink-muted" fontSize="inherit" />
                        Our Advantages ({competitor.advantages.length})
                      </h4>
                      <button
                        type="button"
                        onClick={() => setAdvantageModal({
                          isOpen: true,
                          competitorId: competitor._id,
                          advantage: null
                        })}
                        className="px-3 py-1 bg-signature text-on-signature hover:bg-signature-strong rounded text-sm"
                      >
                        <PlusIcon fontSize="small" className="mr-1" />
                        Add Advantage
                      </button>
                    </div>
                    <div className="space-y-2">
                      {competitor.advantages.map((advantage) => (
                        <div key={advantage._id} className="flex items-start bg-surface p-3 rounded border">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{advantage.point}</p>
                            {advantage.evidence && (
                              <p className="text-sm text-ink-secondary mt-1">Evidence: {advantage.evidence}</p>
                            )}
                            <span className="text-xs text-ink-muted">{advantage.category}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="tertiary"
                              size="sm"
                              aria-label={`Edit advantage ${advantage.point}`}
                              onClick={() => setAdvantageModal({
                                isOpen: true,
                                competitorId: competitor._id,
                                advantage: advantage as CompetitorAdvantage
                              })}
                              className="p-1 text-signature hover:text-signature-strong"
                            >
                              <PencilIcon fontSize="small" />
                            </Button>
                            <Button
                              type="button"
                              variant="tertiary"
                              size="sm"
                              aria-label={`Remove advantage ${advantage.point}`}
                              onClick={() => handleRemoveAdvantage(competitor._id, advantage._id!)}
                              className="p-1 text-error hover:opacity-80"
                            >
                              <TrashIcon fontSize="small" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Weaknesses */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="flex items-center text-base font-medium text-ink">
                        <TrendingDownIcon className="mr-2 text-ink-muted" fontSize="inherit" />
                        Their Weaknesses ({competitor.weaknesses.length})
                      </h4>
                      <button
                        type="button"
                        onClick={() => setWeaknessModal({
                          isOpen: true,
                          competitorId: competitor._id,
                          weakness: null
                        })}
                        className="px-3 py-1 bg-signature text-on-signature hover:bg-signature-strong rounded text-sm"
                      >
                        <PlusIcon fontSize="small" className="mr-1" />
                        Add Weakness
                      </button>
                    </div>
                    <div className="space-y-2">
                      {competitor.weaknesses.map((weakness) => (
                        <div key={weakness._id} className="flex items-start bg-surface p-3 rounded border">
                          <div className="flex-1">
                            <p className="text-sm font-medium">{weakness.point}</p>
                            <span className="text-xs text-ink-muted">{weakness.category}</span>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              type="button"
                              variant="tertiary"
                              size="sm"
                              aria-label={`Edit weakness ${weakness.point}`}
                              onClick={() => setWeaknessModal({
                                isOpen: true,
                                competitorId: competitor._id,
                                weakness: weakness as CompetitorWeakness
                              })}
                              className="p-1 text-signature hover:text-signature-strong"
                            >
                              <PencilIcon fontSize="small" />
                            </Button>
                            <Button
                              type="button"
                              variant="tertiary"
                              size="sm"
                              aria-label={`Remove weakness ${weakness.point}`}
                              onClick={() => handleRemoveWeakness(competitor._id, weakness._id!)}
                              className="p-1 text-error hover:opacity-80"
                            >
                              <TrashIcon fontSize="small" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Detection Info */}
                  <div className="bg-info-tint border border-info rounded-lg p-3">
                    <p className="text-sm text-ink">
                      <strong>Detection:</strong> Competitor mentions are detected using AI-powered LLM matching, which automatically handles misspellings, phonetic variations, and speech-to-text errors.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Advantage Modal */}
      <AdvantageModal
        isOpen={advantageModal.isOpen}
        onClose={() => setAdvantageModal({ isOpen: false, competitorId: null, advantage: null })}
        onSave={handleSaveAdvantage}
        advantage={advantageModal.advantage}
        title={advantageModal.advantage ? 'Edit Advantage' : 'Add Advantage'}
      />

      {/* Weakness Modal */}
      <WeaknessModal
        isOpen={weaknessModal.isOpen}
        onClose={() => setWeaknessModal({ isOpen: false, competitorId: null, weakness: null })}
        onSave={handleSaveWeakness}
        weakness={weaknessModal.weakness}
        title={weaknessModal.weakness ? 'Edit Weakness' : 'Add Weakness'}
      />
    </>
  );
};

export default CompetitorManagement;
